import { io } from "socket.io-client";
import { getErpBaseUrl, getErpToken } from "../erpClient";

/**
 * Centralized Socket.IO client (socket.io-client v4.x) for the ERP SPA.
 *
 * **Origin:** Same scheme/host as REST — from {@link getErpBaseUrl} (pin-aware `/api` vs
 * `/quote`), so sockets follow `REACT_APP_API_URL` / `REACT_APP_QUOTE_API_URL` and match
 * the Fastify + Socket.IO server on one port (e.g. 4050).
 *
 * **Target shape:** `io('<origin>', { path: '/socket.io/', transports: ['websocket','polling'], ... })`
 * e.g. `https://mvc-node-api.gurusoftware.cloud` when env points there.
 *
 * **Nginx (or similar) in front of Node:** proxy `/socket.io/` to the same upstream as REST.
 * Use HTTP/1.1 toward upstream and forward WebSocket upgrade:
 * `proxy_http_version 1.1;`, `proxy_set_header Upgrade $http_upgrade;`,
 * `proxy_set_header Connection "upgrade";`, plus `Host`, `X-Forwarded-For`, `X-Forwarded-Proto`.
 *
 * **Mixed content:** If the SPA is loaded over `https:`, the socket origin must be `https:`
 * (WSS). Keep `REACT_APP_API_URL` on `https://...`, not `http://...`, unless TLS terminates
 * only at the proxy and the browser still speaks HTTPS to the edge.
 *
 * **DevTools:** Filter Network by `socket.io` — polling (GET/POST `transport=polling`) then
 * WebSocket (`transport=websocket`, status 101). On success, `connect` fires and
 * `socket.connected === true`.
 */
export function getSocketIoOrigin() {
  const base = String(getErpBaseUrl() || "").trim();
  if (!base) {
    return typeof window !== "undefined" ? window.location.origin : "";
  }
  try {
    const normalized = /^https?:\/\//i.test(base) ? base : `https://${base}`;
    const u = new URL(normalized.endsWith("/") ? normalized : `${normalized}/`);
    return u.origin;
  } catch {
    return typeof window !== "undefined" ? window.location.origin : "";
  }
}

/** Matches server `path: '/socket.io/'` and socket.io v4. */
export function getDefaultSocketIoClientOptions() {
  return {
    path: "/socket.io/",
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1000,
  };
}

/** `io(origin, options)` aligned with the ERP API host and server Socket.IO path. */
export function createErpSocket(extraOptions = {}) {
  const token = getErpToken();
  return io(getSocketIoOrigin(), {
    ...getDefaultSocketIoClientOptions(),
    ...(token && !extraOptions.auth ? { auth: { token } } : {}),
    ...extraOptions,
  });
}
